import { Text } from "./Text";
/**
 * Поле ввода размера в миллиметрах.
 * Без `unit` поле — длина: показывает и принимает число в единице, выбранной в редакторе
 * (мм, см, м, дюймы), а наружу всегда отдаёт целые миллиметры. С `unit` («°») — как есть.
 *
 * Значение применяется само, пока пользователь печатает: через короткую паузу после
 * последней цифры — без Enter. Пауза нужна, чтобы промежуточное «12» из «1200» не
 * успело поменять чертёж и не оставило в истории шаг на каждую цифру. Число вне
 * пределов при печати не применяется — его подрежут Enter или потеря фокуса.
 * Стрелки-счётчик тоже меняют значение через ту же паузу.
 */
import { useEffect, useRef, useState } from "react";
import { LENGTH_UNITS, fromLengthUnit, toLengthUnit } from "@/shared/lib/geometry";
import { useLengthUnit } from "@/shared/store/lengthUnitStore";

/** Пауза после последнего нажатия, после которой введённое уходит в чертёж, мс. */
const LIVE_APPLY_DELAY_MS = 350;

interface Props {
	label: string;
	value: number;
	onChange: (value: number) => void;
	/** Единица измерения для подписи; без неё поле — длина в единице редактора */
	unit?: string;
	min?: number;
	max?: number;
	step?: number;
	disabled?: boolean;
}

export const NumberField = ({
	label,
	value,
	onChange,
	unit,
	min,
	max,
	step = 10,
	disabled,
}: Props) => {
	const lengthUnit = useLengthUnit();
	const isLength = unit === undefined;
	/** Значение модели → число в поле */
	const shown = (v: number) => (isLength ? toLengthUnit(v, lengthUnit) : Math.round(v));
	/** Число из поля → значение модели */
	const toModel = (v: number) => (isLength ? fromLengthUnit(v, lengthUnit) : Math.round(v));
	const [draft, setDraft] = useState(String(shown(value)));
	const input = useRef<HTMLInputElement>(null);
	/** Что уже отдано наружу — чтобы пауза, blur и Enter не отправили одно и то же дважды. */
	const applied = useRef(Math.round(value));
	const liveTimer = useRef<number | null>(null);

	// Значение поменяли снаружи (пресет толщины, другой тип стены, другая выделенная
	// стена) или сменили единицу — показываем новое. Пока поле в фокусе, черновик не трогаем.
	useEffect(() => {
		if (document.activeElement === input.current) return;
		applied.current = Math.round(value);
		setDraft(String(isLength ? toLengthUnit(value, lengthUnit) : Math.round(value)));
	}, [value, isLength, lengthUnit]);

	const cancelLiveApply = () => {
		if (liveTimer.current !== null) window.clearTimeout(liveTimer.current);
		liveTimer.current = null;
	};

	// Поле убрали (сменилось выделение) — отложенное применение отменяем
	useEffect(
		() => () => {
			if (liveTimer.current !== null) window.clearTimeout(liveTimer.current);
		},
		[],
	);

	/**
	 * Отдаёт введённое наружу. `final` — ввод закончен (Enter, потеря фокуса): число
	 * подрезается до пределов, а неверный ввод откатывается к текущему значению.
	 */
	const apply = (raw: string, final: boolean) => {
		const parsed = Number(raw.replace(",", "."));
		if (raw.trim() === "" || !Number.isFinite(parsed)) {
			if (final) setDraft(String(shown(value)));
			return;
		}
		// Пределы — в единицах модели (мм)
		const model = toModel(parsed);
		const outOfRange = (min !== undefined && model < min) || (max !== undefined && model > max);
		if (outOfRange && !final) return;
		let next = model;
		if (min !== undefined) next = Math.max(min, next);
		if (max !== undefined) next = Math.min(max, next);

		const rounded = Math.round(next);
		if (final) setDraft(String(shown(rounded)));
		if (rounded === applied.current) return;
		applied.current = rounded;
		onChange(rounded);
	};

	const commit = () => {
		cancelLiveApply();
		apply(draft, true);
	};

	return (
		<label className="flex items-center justify-between gap-2.5">
			<Text as="span" className="text-sm text-ink">
				{label}
			</Text>
			<Text as="span" className="flex items-center gap-1">
				<input
					ref={input}
					type="number"
					className="w-21 rounded-md border border-border bg-surface px-2 py-1.5 text-right text-sm text-ink focus:border-accent focus:shadow-focus-accent focus:outline-none"
					value={draft}
					step={isLength ? step / LENGTH_UNITS[lengthUnit].mm : step}
					disabled={disabled}
					onChange={(e) => {
						const raw = e.target.value;
						setDraft(raw);
						cancelLiveApply();
						liveTimer.current = window.setTimeout(() => {
							liveTimer.current = null;
							apply(raw, false);
						}, LIVE_APPLY_DELAY_MS);
					}}
					onBlur={commit}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.currentTarget.blur();
						}
					}}
				/>
				<Text as="span" className="w-5.5 text-xs text-ink">
					{unit ?? LENGTH_UNITS[lengthUnit].suffix}
				</Text>
			</Text>
		</label>
	);
};
